import React from "react";
import { Box, Popper, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

/** Above actions-list Paper (`zIndex: 9999999999999` in style.ts). */
const DIRECT_QR_HANDLER_INFO_POPPER_Z_INDEX = 10_000_000_000_000;

const DirectQrHandlerInfoStyledPopper = styled(Popper)(() => ({
  zIndex: DIRECT_QR_HANDLER_INFO_POPPER_Z_INDEX,
}));

const INFO_POPPER_MODIFIERS = [
  { name: "offset", options: { offset: [0, 8] } },
  { name: "preventOverflow", options: { padding: 8, boundary: "viewport" } },
  { name: "flip", enabled: true },
];

const DirectQrHandlerInfoTooltip = () => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = React.useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setAnchorEl(null);
      closeTimerRef.current = null;
    }, 150);
  }, [clearCloseTimer]);

  React.useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={t("productionFloor.useDirectQrHandlerInfoAria")}
        aria-expanded={open}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => {
          clearCloseTimer();
          setAnchorEl(e.currentTarget as HTMLElement);
        }}
        onMouseLeave={scheduleClose}
        onFocus={(e) => {
          clearCloseTimer();
          setAnchorEl(e.currentTarget as HTMLElement);
        }}
        onBlur={scheduleClose}
        sx={{
          border: "none",
          background: "transparent",
          padding: 0,
          margin: 0,
          cursor: "help",
          display: "inline-flex",
          alignItems: "center",
          lineHeight: 0,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, color: "#718096" }} />
      </Box>
      <DirectQrHandlerInfoStyledPopper
        open={open}
        anchorEl={anchorEl}
        placement="top-start"
        disablePortal={false}
        container={
          typeof document !== "undefined" ? document.body : undefined
        }
        modifiers={INFO_POPPER_MODIFIERS}
        sx={{
          zIndex: `${DIRECT_QR_HANDLER_INFO_POPPER_Z_INDEX} !important`,
          pointerEvents: open ? "auto" : "none",
        }}
        style={{ zIndex: DIRECT_QR_HANDLER_INFO_POPPER_Z_INDEX }}
      >
        <Box
          role="tooltip"
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
          onClick={(e) => e.stopPropagation()}
          sx={{
            maxWidth: 320,
            px: "10px",
            py: "8px",
            borderRadius: "8px",
            bgcolor: "var(--theme-bg-primary, #ffffff)",
            color: "var(--theme-text-primary, #1a202c)",
            fontSize: 12,
            lineHeight: 1.45,
            border: "1px solid var(--theme-border-dark, #d1d5db)",
            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
            pointerEvents: "auto",
          }}
        >
          <Typography
            component="p"
            sx={{ fontWeight: 600, m: 0, mb: 0.5, fontSize: 12 }}
          >
            {t("productionFloor.useDirectQrHandlerInfoOffTitle")}
          </Typography>
          <Typography component="p" sx={{ m: 0, mb: 1, fontSize: 12 }}>
            {t("productionFloor.useDirectQrHandlerInfoOffBody")}
          </Typography>
          <Typography
            component="p"
            sx={{ fontWeight: 600, m: 0, mb: 0.5, fontSize: 12 }}
          >
            {t("productionFloor.useDirectQrHandlerInfoOnTitle")}
          </Typography>
          <Typography component="p" sx={{ m: 0, fontSize: 12 }}>
            {t("productionFloor.useDirectQrHandlerInfoOnBody")}
          </Typography>
        </Box>
      </DirectQrHandlerInfoStyledPopper>
    </>
  );
};

export { DirectQrHandlerInfoTooltip };
